// Разбор чисел из распознанного текста. Мир понимает ТОЛЬКО числа —
// это не свободный NLP. Нечёткое сравнение применяется лишь к словам-числам.

const UNITS: Record<string, number> = {
  "ноль": 0, "нуль": 0,
  "раз": 1, "один": 1, "одна": 1, "одно": 1,
  "два": 2, "две": 2,
  "три": 3,
  "четыре": 4,
  "пять": 5,
  "шесть": 6,
  "семь": 7,
  "восемь": 8,
  "девять": 9,
  "десять": 10,
  "одиннадцать": 11, "двенадцать": 12, "тринадцать": 13, "четырнадцать": 14,
  "пятнадцать": 15, "шестнадцать": 16, "семнадцать": 17, "восемнадцать": 18,
  "девятнадцать": 19,
};

const TENS: Record<string, number> = {
  "двадцать": 20, "тридцать": 30, "сорок": 40, "пятьдесят": 50,
  "шестьдесят": 60, "семьдесят": 70, "восемьдесят": 80, "девяносто": 90,
};

const HUNDRED: Record<string, number> = { "сто": 100 };

// Детские варианты произношения → канон (GDD 6.1).
const CHILD_SYNONYMS: Record<string, string> = {
  "четыле": "четыре", "чатыри": "четыре", "читыре": "четыре", "четыри": "четыре",
  "тли": "три", "тви": "два", "тэри": "три",
  "сесть": "шесть", "шесь": "шесть",
  "восимь": "восемь", "восем": "восемь",
  "девить": "девять", "деветь": "девять", "девядь": "девять",
  "десить": "десять", "десят": "десять", "десит": "десять",
  "пат": "пять", "петь": "пять",
  "сем": "семь",
  "адин": "один", "адна": "одна",
};

const ALL_WORDS = [...Object.keys(UNITS), ...Object.keys(TENS), ...Object.keys(HUNDRED)];

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (Math.abs(m - n) > 2) return 3;
  const dp = Array.from({ length: m + 1 }, (_, i) => i);
  for (let j = 1; j <= n; j++) {
    let prev = dp[0];
    dp[0] = j;
    for (let i = 1; i <= m; i++) {
      const tmp = dp[i];
      dp[i] = Math.min(
        dp[i] + 1,
        dp[i - 1] + 1,
        prev + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      prev = tmp;
    }
  }
  return dp[m];
}

// Нечёткое сравнение применяется ТОЛЬКО к словам-числам и намеренно строгое,
// чтобы обычная речь («давай», «привет») не превращалась в числа.
//   • точное совпадение;
//   • детский синоним;
//   • строгий префикс ровно одного слова-числа (токен ≥ 4, «хвост» ≤ 3 букв);
//   • Левенштейн: разница длин ≤ 1; порог 1 для коротких слов, 2 — если
//     длиннейшее из слов ≥ 6 букв (четыре/восемь/девять/десять…).
function canonWord(token: string): string | null {
  if (UNITS[token] !== undefined || TENS[token] !== undefined || HUNDRED[token] !== undefined) return token;
  if (CHILD_SYNONYMS[token]) return CHILD_SYNONYMS[token];

  const byPrefix = ALL_WORDS.filter(
    (w) => w !== token && w.startsWith(token) && token.length >= 4 && w.length - token.length <= 3,
  );
  if (byPrefix.length === 1) return byPrefix[0];

  let best: string | null = null;
  let bestD = 99;
  for (const w of ALL_WORDS) {
    if (Math.abs(w.length - token.length) > 1) continue;
    const d = levenshtein(token, w);
    if (d < bestD) {
      bestD = d;
      best = w;
    }
  }
  if (!best) return null;
  const allowed = Math.max(best.length, token.length) >= 6 ? 2 : 1;
  return bestD <= allowed ? best : null;
}

function wordValue(canon: string): number | null {
  if (UNITS[canon] !== undefined) return UNITS[canon];
  if (TENS[canon] !== undefined) return TENS[canon];
  if (HUNDRED[canon] !== undefined) return HUNDRED[canon];
  return null;
}

/**
 * Извлечь все числа из фразы по порядку.
 * «пять, шесть, семь» → [5,6,7]; «двадцать три» → [23]; «семь» → [7].
 */
export function parseNumbers(text: string): number[] {
  if (!text) return [];
  const norm = text
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[^a-zа-я0-9\s-]/gi, " ")
    .replace(/-/g, " ")
    .trim();
  if (!norm) return [];

  const tokens = norm.split(/\s+/);
  const out: number[] = [];
  let pendingTen = 0; // накопитель десятков для составных чисел

  const flushTen = () => {
    if (pendingTen) {
      out.push(pendingTen);
      pendingTen = 0;
    }
  };

  for (const tok of tokens) {
    if (/^\d+$/.test(tok)) {
      flushTen();
      const v = parseInt(tok, 10);
      if (v >= 0 && v <= 100) out.push(v);
      continue;
    }
    const canon = canonWord(tok);
    if (!canon) continue;
    const val = wordValue(canon);
    if (val === null) continue;

    if (TENS[canon] !== undefined) {
      flushTen();
      pendingTen = val;
    } else if (HUNDRED[canon] !== undefined) {
      flushTen();
      out.push(100);
    } else {
      // единица / число 0..19
      if (pendingTen && val >= 1 && val <= 9) {
        out.push(pendingTen + val);
        pendingTen = 0;
      } else {
        flushTen();
        out.push(val);
      }
    }
  }
  flushTen();
  return out;
}

/** Первое число во фразе (или null). */
export function firstNumber(text: string): number | null {
  const n = parseNumbers(text);
  return n.length ? n[0] : null;
}
